import React, { useContext, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Typography,
  Divider,
  Box,
  Link,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import ReactMarkdown from "react-markdown";
import { AuthContext } from "../../context/auth-context";

// Set to true when this feature is ready to be shown to users
const IS_DISPLAY = true;

/**
 * RawSearchInfoButton
 *
 * Props:
 *   data: { text: string (markdown), sources: string[] }
 *   label: string — optional dialog title (default "Search Info")
 *
 * Only renders when IS_DISPLAY is true AND the current user is admin.
 */
function RawSearchInfoButton({ data, label = "Search Info" }) {
  const [open, setOpen] = useState(false);
  const { user } = useContext(AuthContext);
  const isCurrentUserAdmin = user?.user_role?.role?.name === "Company admin";

  if (!IS_DISPLAY || !isCurrentUserAdmin) return null;
  if (!data?.text && (!data?.sources || data.sources.length === 0)) return null;

  return (
    <>
      <IconButton
        size="small"
        onClick={(e) => {
          e.stopPropagation();
          setOpen(true);
        }}
        sx={{ ml: 0.5, color: "text.secondary" }}
        title={label}
      >
        <InfoOutlinedIcon fontSize="small" />
      </IconButton>

      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        maxWidth="md"
        fullWidth
        onClick={(e) => e.stopPropagation()}
      >
        <DialogTitle sx={{ pb: 1 }}>{label}</DialogTitle>
        <Divider />
        <DialogContent>
          {data?.text && (
            <Box
              sx={{
                "& p": { mt: 0, mb: 1 },
                "& ul, & ol": { pl: 2.5, mb: 1 },
                "& li": { mb: 0.25 },
                "& h1,& h2,& h3,& h4": { mt: 1.5, mb: 0.5 },
                "& strong": { fontWeight: 600 },
                "& a": { color: "primary.main", wordBreak: "break-all" },
                fontSize: "0.875rem",
                lineHeight: 1.6,
              }}
            >
              <ReactMarkdown>{data.text}</ReactMarkdown>
            </Box>
          )}

          {data?.sources?.length > 0 && (
            <>
              {data?.text && <Divider sx={{ my: 1.5 }} />}
              <Typography variant="subtitle2" gutterBottom>
                Sources
              </Typography>
              <Box component="ul" sx={{ pl: 2.5, m: 0 }}>
                {data.sources.map((src, i) => (
                  <Box component="li" key={i} sx={{ mb: 0.25 }}>
                    <Link
                      href={src}
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{ fontSize: "0.8rem", wordBreak: "break-all" }}
                    >
                      {src}
                    </Link>
                  </Box>
                ))}
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default RawSearchInfoButton;
